import React, { useState } from "react";

import { MobileStepper, Button } from "@mui/material";
import { ArrowForward } from "@mui/icons-material";
import theme from "../../Theme";
import Cookies from "js-cookie";

import { HelpTooltipContainer } from "./styles";

import DefaultButton from "../simple/DefaultButton";

import Step1Image from "../../assets/help-tooltip/step-1.png";
import Step2Image from "../../assets/help-tooltip/step-2.png";
import Step3Image from "../../assets/help-tooltip/step-3.png";
import Step4Image from "../../assets/help-tooltip/step-4.png";
import Step5Image from "../../assets/help-tooltip/step-5.png";
import Step6Image from "../../assets/help-tooltip/step-6.png";

const HelpTooltip = ({ isNewUser, mediaAccess, handleClose }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const steps = [
    {
      title: !isNewUser ? "Welcome to GuruNow" : "Need a Refresher?",
      text: !isNewUser
        ? "This is your first simulation - exciting! Let's take a quick tour so you know where everything is and how to get the most out of your practice."
        : "If you'd like a quick overview, you can watch our tutorial video for a full walkthrough of GuruNow. Or, take this quick tour to remind yourself where everything is.",
      mediaType: "image", // convert to if/else later
      imageSrc: Step1Image, // convert to if/else later
      skippable: true,
    },
    {
      title: "Check Your Audio",
      text: "Your voice is the most important part of the simulation, so be sure you've allowed microphone access in your browser and computer settings. If your Guru can't hear you, it can't respond!",
      mediaType: "image",
      imageSrc: Step2Image,
      skippable: mediaAccess?.microphone ? true : false,
    },
    {
      title: "Turn Your Camera On",
      text: "Make sure you've accepted camera permissions in your browser and laptop settings. Switch your video on in the control bar to make the simulation feel like a real conversation.",
      mediaType: "image",
      imageSrc: Step3Image,
      skippable: mediaAccess?.camera ? true : false,
    },
    {
      title: "Follow the Transcript",
      text: "Open this panel to see a live transcript of the conversation. It's helpful if you want to double-check what was said or keep track of key points as you go.",
      mediaType: "image",
      imageSrc: Step4Image,
      skippable: true,
    },
    {
      title: "See How You Can Improve",
      text: "If you've taken this simulation before, you'll see recommendations here based on your past attempts. They highlight where you can improve, helping you track progress over time.",
      mediaType: "image",
      imageSrc: Step5Image,
      skippable: true,
    },
    {
      title: "You're ready to start!",
      text: "Speak as if you're in a real conversation. Don't worry about being perfect — the goal is to practise and learn. The more you use simulations, the more natural it will feel.",
      mediaType: "image",
      imageSrc: Step6Image,
      skippable: true,
    },
  ];

  const nextStepButtonAction = () => {
    if (currentStep === steps.length - 1) {
      setCurrentStep(0);
      const isSecure = window.location.protocol === "https:";
      Cookies.set("GN-CompletedSim", "true", {
        expires: 365,
        secure: isSecure, // only force secure on HTTPS
        sameSite: isSecure ? "None" : "Lax", // cross-site safe if secure, fallback for dev
      });
      handleClose();
    }
    // check for skippable here and do nothing if the skippable prop is false
    if (steps[currentStep].skippable) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const backButtonAction = () => {
    if (currentStep === 0) return;
    setCurrentStep((prev) => prev - 1);
  };

  const nextStepButtonLabel = () => {
    if (!isNewUser && currentStep === 0) {
      return "Next";
    } else if (isNewUser && currentStep === 0) {
      return "Take the tour";
    } else if (currentStep + 1 === steps.length) {
      return "Let's go";
    } else {
      return "Next";
    }
  };

  return (
    <HelpTooltipContainer className="help-tooltip-container">
      {(isNewUser === "true"  ||
        (isNewUser && currentStep + 1 === steps.length)) && (
        <button className="close" onClick={() => handleClose()}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M15 5L5 15M5 5L15 15"
              stroke="#FEFEFE"
              strokeWidth="1.66667"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}
      <div className="tooltip-content">
        <h2>{steps[currentStep].title}</h2>
        <p>{steps[currentStep].text}</p>
      </div>
      {steps[currentStep].mediaType === "image" && (
        <img src={steps[currentStep].imageSrc} alt="" />
      )}
      <MobileStepper
        variant="dots"
        steps={steps.length}
        position="static"
        activeStep={currentStep}
        sx={{
          backgroundColor: "transparent",
          justifyContent: "space-between",
          margin: "15px 0 20px 0",
          padding: 0,
          display: "flex",
          width: "100%",
          "& .MuiMobileStepper-dots": {
            width: "100%",
            gap: "5px",
          },
          ".MuiMobileStepper-dot": {
            backgroundColor: theme.palette.foreground.brand_primary,
            flex: 1,
            textAlign: "center",
            padding: "0.2rem 1rem",
            borderRadius: "9999px",
          },
          ".MuiMobileStepper-dotActive": { backgroundColor: "white" },
        }}
      />
      <div className="tooltip-actions">
        <div className="counter">
          {currentStep + 1} of {steps.length}
        </div>
        <div className="buttons">
          {currentStep !== 0 && (
            <Button
              onClick={backButtonAction}
              variant="contained"
              sx={{
                bgcolor: "white",
                color: "#000",
                textTransform: "none",
                "&:hover": { bgcolor: "#f0f0f0" },
              }}
            >
              Back
            </Button>
          )}
          <DefaultButton
            size="sm"
            onClick={nextStepButtonAction}
            endIcon={
              <ArrowForward
                sx={{ fontSize: theme.typography.textLG.fontSize }}
              />
            }
          >
            {nextStepButtonLabel()}
          </DefaultButton>
        </div>
      </div>
    </HelpTooltipContainer>
  );
};

export default HelpTooltip;
